import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useDialog } from '@/common/context/DialogContext';
import { processApiError } from '@/common/utils/apiErrorReporter';
import {
  getListConnectionsQueryKey,
  useAcceptConnection,
  useDeclineConnection,
  useRemoveConnection,
  useRequestConnection,
  useWithdrawConnection,
} from '@/common/api/endpoints/connections/connections';
import type { ConnectionView } from '@/common/api/models/connectionView';

import { invalidateAllGssOccasionPeopleQueries } from '../peopleContact';

type RowAction = 'accept' | 'decline' | 'withdraw';
type OccasionRowAction = 'request';

/**
 * Connection mutations, remove confirm dialog, and per-row error state.
 */
export function useConnectionActions() {
  const { t } = useTranslation();
  const { showDialog, showToastMessage } = useDialog();
  const queryClient = useQueryClient();
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [pendingRowId, setPendingRowId] = useState<string | null>(null);
  const [lastFailedAction, setLastFailedAction] = useState<Record<string, RowAction>>({});
  const [occasionRowErrors, setOccasionRowErrors] = useState<Record<string, string>>({});
  const [occasionUnavailable, setOccasionUnavailable] = useState<Record<string, boolean>>({});
  const [pendingOccasionTargetId, setPendingOccasionTargetId] = useState<string | null>(null);
  const [lastFailedOccasionAction, setLastFailedOccasionAction] = useState<Record<string, OccasionRowAction>>({});

  const invalidateList = useCallback(
    () => queryClient.invalidateQueries({ queryKey: getListConnectionsQueryKey() }),
    [queryClient],
  );

  const invalidateOccasionLists = useCallback(
    () => invalidateAllGssOccasionPeopleQueries(queryClient),
    [queryClient],
  );

  const clearRowError = useCallback((connectionId: string) => {
    setRowErrors((prev) => {
      if (!prev[connectionId]) return prev;
      const next = { ...prev };
      delete next[connectionId];
      return next;
    });
  }, []);

  const setRowError = useCallback((connectionId: string) => {
    setRowErrors((prev) => ({
      ...prev,
      [connectionId]: t('people.row.errorAction'),
    }));
  }, [t]);

  const clearOccasionRowError = useCallback((targetUserId: string) => {
    setOccasionRowErrors((prev) => {
      if (!prev[targetUserId]) return prev;
      const next = { ...prev };
      delete next[targetUserId];
      return next;
    });
  }, []);

  const setOccasionRowError = useCallback((targetUserId: string) => {
    setOccasionRowErrors((prev) => ({
      ...prev,
      [targetUserId]: t('people.row.errorSend'),
    }));
  }, [t]);

  const acceptMutation = useAcceptConnection({
    mutation: {
      onMutate: ({ connectionId }) => {
        setPendingRowId(connectionId);
        clearRowError(connectionId);
      },
      onSuccess: async () => {
        await invalidateList();
        await invalidateOccasionLists();
      },
      onError: (_err, { connectionId }) => {
        setRowError(connectionId);
        setLastFailedAction((prev) => ({ ...prev, [connectionId]: 'accept' }));
      },
      onSettled: () => setPendingRowId(null),
    },
  });

  const declineMutation = useDeclineConnection({
    mutation: {
      onMutate: ({ connectionId }) => {
        setPendingRowId(connectionId);
        clearRowError(connectionId);
      },
      onSuccess: async () => {
        await invalidateList();
        await invalidateOccasionLists();
      },
      onError: (_err, { connectionId }) => {
        setRowError(connectionId);
        setLastFailedAction((prev) => ({ ...prev, [connectionId]: 'decline' }));
      },
      onSettled: () => setPendingRowId(null),
    },
  });

  const withdrawMutation = useWithdrawConnection({
    mutation: {
      onMutate: ({ connectionId }) => {
        setPendingRowId(connectionId);
        clearRowError(connectionId);
      },
      onSuccess: async () => {
        await invalidateList();
        await invalidateOccasionLists();
      },
      onError: (_err, { connectionId }) => {
        setRowError(connectionId);
        setLastFailedAction((prev) => ({ ...prev, [connectionId]: 'withdraw' }));
      },
      onSettled: () => setPendingRowId(null),
    },
  });

  const removeMutation = useRemoveConnection({
    mutation: {
      onMutate: ({ connectionId }) => setPendingRowId(connectionId),
      onSuccess: async () => {
        await invalidateList();
        await invalidateOccasionLists();
      },
      onError: () => {
        showToastMessage('error', t('people.connections.remove.error'));
      },
      onSettled: () => setPendingRowId(null),
    },
  });

  const requestMutation = useRequestConnection({
    mutation: {
      onMutate: ({ data }) => {
        setPendingOccasionTargetId(data.targetUserId);
        clearOccasionRowError(data.targetUserId);
      },
      onSuccess: async () => {
        await invalidateList();
        await invalidateOccasionLists();
      },
      onError: async (err, { data }) => {
        const { code } = processApiError(err);
        await invalidateOccasionLists();
        if (code === 'CONNECTION_NOT_ALLOWED') {
          setOccasionUnavailable((prev) => ({ ...prev, [data.targetUserId]: true }));
          return;
        }
        if (code === 'CONNECTION_CONFLICT') {
          await invalidateList();
          return;
        }
        setOccasionRowError(data.targetUserId);
        setLastFailedOccasionAction((prev) => ({ ...prev, [data.targetUserId]: 'request' }));
      },
      onSettled: () => setPendingOccasionTargetId(null),
    },
  });

  const accept = useCallback((
    connectionId: string,
    options?: { acceptedToastName?: string; onViewContact?: () => void },
  ) => {
    acceptMutation.mutate(
      { connectionId },
      {
        onSuccess: () => {
          if (!options?.acceptedToastName) return;
          showToastMessage(
            'success',
            t('people.toast.accepted', { name: options.acceptedToastName }),
            '',
            'top',
            6000,
            'accent',
            options.onViewContact
              ? {
                label: t('people.row.viewContact'),
                onPress: options.onViewContact,
                testID: 'connection-accepted-toast-view',
              }
              : undefined,
          );
        },
      },
    );
  }, [acceptMutation, showToastMessage, t]);

  const decline = useCallback((connectionId: string) => {
    declineMutation.mutate({ connectionId });
  }, [declineMutation]);

  const withdraw = useCallback((connectionId: string) => {
    withdrawMutation.mutate({ connectionId });
  }, [withdrawMutation]);

  const requestFromOccasion = useCallback((targetUserId: string, slotId: string) => {
    requestMutation.mutate({
      data: {
        targetUserId,
        occasion: { type: 'gss_slot', slotId },
      },
    });
  }, [requestMutation]);

  const retryRow = useCallback((connectionId: string) => {
    const action = lastFailedAction[connectionId];
    if (action === 'accept') accept(connectionId);
    else if (action === 'decline') decline(connectionId);
    else if (action === 'withdraw') withdraw(connectionId);
  }, [accept, decline, withdraw, lastFailedAction]);

  const retryOccasionRow = useCallback((targetUserId: string, slotId: string) => {
    const action = lastFailedOccasionAction[targetUserId];
    if (action === 'request') requestFromOccasion(targetUserId, slotId);
  }, [lastFailedOccasionAction, requestFromOccasion]);

  const confirmRemove = useCallback((connection: ConnectionView) => {
    const name = connection.peer.firstName ?? '';
    showDialog({
      title: t('people.connections.remove.title', { name }),
      message: t('people.connections.remove.message', { name }),
      buttons: [
        {
          text: t('common.buttons.cancel'),
          variant: 'outline',
          testID: 'remove-connection-cancel',
        },
        {
          text: t('people.connections.remove.confirm'),
          action: 'negative',
          testID: 'remove-connection-confirm',
          onPress: () => removeMutation.mutate({ connectionId: connection.id }),
        },
      ],
    });
  }, [removeMutation, showDialog, t]);

  const getRowState = useCallback((connection: ConnectionView): 'pendingIn' | 'pendingOut' | 'connected' => {
    if (connection.direction === 'pending_in') return 'pendingIn';
    if (connection.direction === 'pending_out') return 'pendingOut';
    return 'connected';
  }, []);

  const getDisplayState = useCallback((connection: ConnectionView) => {
    if (rowErrors[connection.id]) return 'error' as const;
    return getRowState(connection);
  }, [getRowState, rowErrors]);

  const getOccasionDisplayState = useCallback((
    targetUserId: string,
    serverState: 'none' | 'pendingIn' | 'pendingOut' | 'connected',
  ) => {
    if (occasionUnavailable[targetUserId]) return 'unavailable' as const;
    if (occasionRowErrors[targetUserId]) return 'error' as const;
    if (pendingOccasionTargetId === targetUserId) return 'sending' as const;
    if (serverState === 'none') return 'none' as const;
    return serverState;
  }, [occasionRowErrors, occasionUnavailable, pendingOccasionTargetId]);

  return {
    accept,
    decline,
    withdraw,
    requestFromOccasion,
    confirmRemove,
    retryRow,
    retryOccasionRow,
    getDisplayState,
    getOccasionDisplayState,
    getRowError: (id: string) => rowErrors[id],
    getOccasionRowError: (targetUserId: string) => occasionRowErrors[targetUserId],
    isRowPending: (id: string) => pendingRowId === id,
    isOccasionRowPending: (targetUserId: string) => pendingOccasionTargetId === targetUserId,
  };
}
